import dayjs from "dayjs"
import ParseError from "@/types/ParseError"
import {
  FeatureFlagChangeListResponse,
  FeatureFlagChangeOperation,
  FeatureFlagChangeResponse,
} from "@/api/backofficeRest/generated/model"
import { IFeature } from "@/models/admin/feature/feature.types"
import { FeatureChange } from "./featureChange"
import { IFeatureChange, IFeatureFieldChange } from "./featureChange.types"

/** The fields the history shows, keyed by the labels describeFieldChange knows. */
type FeatureSnapshot = Record<
  "enabled" | "description" | "squad_name" | "deleted",
  string | null
>

const snapshotOf = (state: {
  enabled?: boolean | null
  description?: string | null
  squadName?: string | null
  deleted?: string | null
}): FeatureSnapshot => ({
  enabled: state.enabled == null ? null : String(state.enabled),
  description: state.description ?? null,
  squad_name: state.squadName ?? null,
  deleted: state.deleted ?? null,
})

const fieldChanges = (
  before: FeatureSnapshot | null,
  after: FeatureSnapshot | null,
): IFeatureFieldChange[] =>
  (["enabled", "description", "squad_name", "deleted"] as const)
    .map((field) => ({
      field,
      oldValue: before?.[field] ?? null,
      newValue: after?.[field] ?? null,
    }))
    .filter((change) => change.oldValue !== change.newValue)

/**
 * Each event holds the flag as it was before the change (the inserted row for an
 * INSERT), so the state after an UPDATE is the next newer event for that flag, or
 * the current flag for its latest change.
 */
const toFeatureChange = (
  event: FeatureFlagChangeResponse,
  newer: FeatureFlagChangeResponse | undefined,
  current: IFeature | undefined,
): IFeatureChange => {
  const snapshot = snapshotOf(event)
  let changes: IFeatureFieldChange[]
  if (event.operation === FeatureFlagChangeOperation.INSERT) {
    changes = fieldChanges(null, snapshot)
  } else if (event.operation === FeatureFlagChangeOperation.DELETE) {
    changes = fieldChanges(snapshot, null)
  } else if (newer) {
    changes = fieldChanges(snapshot, snapshotOf(newer))
  } else if (current) {
    changes = fieldChanges(snapshot, snapshotOf(current))
  } else {
    changes = []
  }

  return new FeatureChange(
    event.id,
    event.featureId,
    event.featureName,
    event.operation,
    changes,
    dayjs(event.changedAt),
  )
}

/** All flags' changes, newest first, each with what it changed. */
export const parseFeatureChanges = (
  data: FeatureFlagChangeListResponse,
  currentFeatures: IFeature[],
): IFeatureChange[] => {
  try {
    return Object.values(data.changes)
      .flatMap((events) =>
        events.map((event, i) =>
          toFeatureChange(
            event,
            events[i - 1],
            currentFeatures.find((f) => f.id === event.featureId),
          ),
        ),
      )
      .sort((a, b) => b.id - a.id)
  } catch (error) {
    throw new ParseError(error)
  }
}
